import React, { FC } from "react";
import { Tooltip } from "react-tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLocale } from "i18n/locale";

export interface BookedNightsData {
    property_id: string;
    booked_nights: number;
    quota: number;
    percentage: number;
    /**
     * Language the API reports for the property. No longer decides the copy
     * here — the visitor's own choice in the language switcher does, so the
     * panel follows the rest of the page.
     */
    lang?: string;
}

interface BookedNightsCounterProps {
    data: BookedNightsData;
    year: number;
}

// Styled to match the new site's design system: Poppins, navy #01152E for the
// headline/values, gold #B88333 as the accent (icon + progress fill), #445162
// for secondary copy and a cream track/badge.
const BookedNightsCounter: FC<BookedNightsCounterProps> = ({ data, year }) => {
    const { t } = useLocale();
    const percentage = Math.min(data.percentage, 100);

    return (
        <div className="px-4 sm:min-w-[260px]" style={{ fontFamily: "Poppins, sans-serif" }}>
            <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F1E7D7] flex-shrink-0">
                    <i className="las la-home text-xl text-[#B88333]"></i>
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#01152E]">
                            {t("listingStayDetail.primaryResidenceStatus")}
                        </span>
                        <>
                            <InfoOutlinedIcon fontSize="small"
                                data-tooltip-id="booked-nights-info"
                                data-tooltip-content={t(
                                    "listingStayDetail.primaryResidenceTooltip",
                                    { quota: data.quota }
                                )}
                                className="w-4 h-4 text-[#98A2B3] cursor-pointer"
                            />
                            <Tooltip
                                id="booked-nights-info"
                                place="bottom"
                                style={{ maxWidth: '260px', fontSize: '12px', lineHeight: '1.5', backgroundColor: '#01152E', color: '#ffffff', fontFamily: 'Poppins, sans-serif', zIndex: 9999 }}
                            />
                        </>
                    </div>
                    <span className="text-xs text-[#445162] block leading-none mt-0.5">
                        {t("listingStayDetail.nightsBookedIn", { year })}
                    </span>
                </div>
            </div>

            <div className="flex items-baseline gap-1 mt-2">
                <span className="text-2xl font-bold text-[#01152E]">
                    {data.booked_nights}
                </span>
                <span className="text-sm text-[#445162]">
                    {t("listingStayDetail.outOfNights", { quota: data.quota })}
                </span>
            </div>
            <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-[#EADFCE] rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${percentage}%`,
                            backgroundColor: '#B88333',
                        }}
                    />
                </div>
                <span className="text-sm text-[#445162]">
                    {data.percentage}%
                </span>
            </div>
        </div>
    );
};

export default BookedNightsCounter;